import { useBundle } from '@amzn/react-arb-tools';
import { HEADING_TAG, SECTION_THEME, useDeviceSize } from '@amzn/sss-website-theme';

import carbonCapture from 'src/assets/lotties/carbon-capture-desktop.json';
import { SegmentConfigProps } from 'src/components/pages/segment/types';
import { TECH_CARBON_REMOVAL_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { getImageRoot } from 'src/helpers';
import { t } from 'src/helpers/translations/translation-helpers';

export function useTechCarbonRemovalConfig(): { isBundleLoading: boolean; config: SegmentConfigProps } {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  const [techCarbonRemovalBundle, isTechCarbonRemovalBundleLoading] = useBundle(TECH_CARBON_REMOVAL_BUNDLE_NAME);

  return {
    isBundleLoading: isTechCarbonRemovalBundleLoading,
    config: {
      heroAsset: {
        image: {
          src: `${imageRoot}/credits-segments-tcr-hero-1.jpg`,
          alt: t(techCarbonRemovalBundle, 'heroAsset_image_altText'),
        },
        title: t(techCarbonRemovalBundle, 'heroAsset_title'),
      },

      hasAnimatedGraphic: true,

      dynamicList: {
        mediaProps: {
          mediaTitle: t(techCarbonRemovalBundle, 'dynamicList_mediaTitle'),
          mediaTagline: t(techCarbonRemovalBundle, 'dynamicList_mediaTagline'),
          media: [
            {
              image: {
                src: `${imageRoot}/credits-segments-tcr-ourapproach-1.jpg`,
                alt: t(techCarbonRemovalBundle, 'dynamicList_firstMedia_altText'),
              },
            },
            {
              lottie: {
                animationData: carbonCapture,
                alt: t(techCarbonRemovalBundle, 'dynamicList_secondMedia_altText'),
                ariaLabels: {
                  play: t(techCarbonRemovalBundle, 'dynamicList_secondMedia_ariaLabels_play'),
                  pause: t(techCarbonRemovalBundle, 'dynamicList_secondMedia_ariaLabels_pause'),
                },
              },
            },
          ],
        },
        items: [
          {
            title: t(techCarbonRemovalBundle, 'dynamicList_firstItem_title'),
            body: t(techCarbonRemovalBundle, 'dynamicList_firstItem_body'),
          },
          {
            title: t(techCarbonRemovalBundle, 'dynamicList_secondItem_title'),
            body: t(techCarbonRemovalBundle, 'dynamicList_secondItem_body'),
          },
        ],
      },

      secondaryBentoCards: {
        firstCard: `${imageRoot}/credits-segments-tcr-ourquality-1.jpg`,
        firstCardModal: `${imageRoot}/credits-segments-tcr-ourquality-2.jpg`,
        secondCard: `${imageRoot}/credits-segments-tcr-ourquality-3.jpg`,
        bentoCardProps: {
          label: t(techCarbonRemovalBundle, 'secondaryBentoCards_label'),
          title: t(techCarbonRemovalBundle, 'secondaryBentoCards_title'),
          titleSize: HEADING_TAG.H4,
          description: t(techCarbonRemovalBundle, 'secondaryBentoCards_description'),
          tooltipAttributes: undefined,
          tooltipTheme: SECTION_THEME.Light,
          firstCard: {
            label: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_label'),
            title: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_title'),
            image: {
              src: `${imageRoot}/credits-segments-tcr-ourquality-1.jpg`,
              alt: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_altText'),
            },
            modal: {
              label: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_label'),
              title: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_title'),
              image: {
                src: `${imageRoot}/credits-segments-tcr-ourquality-2.jpg`,
                alt: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_modal_altText'),
              },
              modalContent: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_modal_content'),
              link: {
                url: 'https://www.carboncapture.com/',
                label: t(techCarbonRemovalBundle, 'secondaryBentoCards_firstCard_modal_linkLabel'),
                icon: undefined,
                isSource: false,
              },
            },
          },
          secondCard: {
            src: `${imageRoot}/credits-segments-tcr-ourquality-3.jpg`,
            alt: t(techCarbonRemovalBundle, 'secondaryBentoCards_secondCard_altText'),
          },
        },
      },
    },
  };
}
